/**
 * Synthetic form-only keys that live in the RHF form state but must NEVER be persisted
 * to the backend save payload.
 *
 * Conventions:
 * - All keys start with `_` so `handleSave` can strip them with a single prefix check.
 * - Use the exported constants (not raw strings) everywhere — single source of truth
 *   prevents typos silently leaking to the save payload.
 */

/** Gate flag: true once master-password-encrypted fields have been successfully decrypted. */
export const SYNTHETIC_MASTER_PASSWORD_UNLOCKED = '_masterPasswordUnlocked';

/**
 * Preview-blocked flag: true when a live-preview target URL cannot be embedded in an iframe
 * (X-Frame-Options / CSP `frame-ancestors`), set by the `verifyEmbeddableUrl` action handler when
 * run via `runOnChange`. A manifest `notice` field reads it via
 * `displayOptions.showIf: { _previewBlocked: [true] }` to surface a "preview not available" message
 * (Website app — mirrors the V2 remote form's header-checker behaviour).
 */
export const SYNTHETIC_PREVIEW_BLOCKED = '_previewBlocked';

/**
 * The URL the embeddable check actually ran against — the PREVIEW-NORMALIZED form of the user's
 * input (`lumas.com` → `https://lumas.com`, see `normalize-preview-url.ts`), written by
 * `verifyEmbeddableUrl` alongside `_previewBlocked`. Notice templates link with
 * `href="${_checkedUrl}"` instead of `${webLink}`: a saved schemeless value would render a
 * RELATIVE href that 404s inside the portal.
 */
export const SYNTHETIC_CHECKED_URL = '_checkedUrl';

/**
 * The Power BI page picker's LIVE report page list (multi-page container feature) — what
 * `usePowerBIPages` fetches from the smallapp REST endpoint, never the user's stored
 * selection. Living here (instead of hook `useState`) is the fix for the picker going blank
 * after a save: `mergePersistedFormValues` only overwrites keys the manifest DECLARES and
 * explicitly skips reserved keys (`isReservedFormKey`), so this key is left at its live value
 * across `behavior.syncFormAfterSave`'s post-save re-baseline — a plain `useState` has no such
 * protection and gets wiped the moment the credential fields are transiently rewritten out
 * from under it. See `usePowerBIPages.ts` for the guard-miss-vs-report-change distinction that
 * governs when this key is (and is not) allowed to be cleared.
 */
export const SYNTHETIC_REPORT_PAGES = '_reportPages';

/** True once a Power BI credential (Direct Login token OR Service Principal key) is present on
 *  the form — collapses "DL token present OR integrationKeyId present" into one field `action.requires`
 *  can gate on (that array is AND-only). Kept in sync by `PowerBIPagesField.tsx`. */
export const SYNTHETIC_HAS_POWERBI_CREDENTIAL = '_hasPowerBICredential';

/**
 * Why the Power BI kiosk toggle is currently locked: `''` (not locked), `'pages'` (a page
 * selection exists, so rotation owns this asset) or `'dashboard'` (a dashboard has no pages to
 * switch between, so kiosk mode means nothing there). One string instead of two booleans because
 * `disabled` takes a single ANDed clause — it cannot express "pages OR dashboard" — while
 * `whenSet` on this key covers both and `descriptionCases` can name the specific reason.
 * Kept in sync by `PowerBIPagesField.tsx`, which is the component that knows both facts.
 */
export const SYNTHETIC_KIOSK_LOCK_REASON = '_kioskLockReason';

/**
 * `'pages'` while the Power BI page picker holds a selection, `''` otherwise. With pages
 * selected each page refreshes by its page duration (one page: the player reloads it; 2+: the
 * rotation does), so the manifest locks Update Interval on it. Kept in sync by
 * `PowerBIPagesField.tsx`.
 */
export const SYNTHETIC_POWERBI_PAGES_SELECTED = '_powerBIPagesSelected';

/** The Power BI content picker's resolved-name helper text (e.g. "Marketing / Q3 Sales Report"),
 *  written by the `openPowerBIBrowseModal` action handler and read by `StringField` via
 *  `field.action.resolvedLabelKey`. */
export const SYNTHETIC_POWERBI_RESOLVED_LABEL = '_powerBIResolvedLabel';

/** Why the dashboard the grafana "Paste a dashboard link" dialog just handed to the dropdown is
 *  not in its list: `'forbidden'` (the connection's token cannot view it, measured) or
 *  `'unreachable'` (our servers could not reach or confirm). Gates one notice each in the grafana
 *  manifest. Written by `GrafanaDashboardRefDialog`, cleared to `''` by the dropdown's
 *  `setValues` fan-out when the list holds the uid. Never persisted, so never set on reopen. */
export const SYNTHETIC_GRAFANA_DASHBOARD_UNLISTED = '_dashboardUnlisted';

/**
 * Source media length in SECONDS for the url currently in the form, read by `time-offset` fields
 * to range-check and echo. Seeded by `ConfigEngineDialog` from `asset.duration` and kept in step
 * with the url by `useSourceDuration`. Not a manifest field, so it never reaches a save payload;
 * absent whenever the length is unknown, and the field degrades rather than inventing one.
 */
export const SYNTHETIC_ASSET_DURATION = '_assetDuration';

/** All synthetic keys (for validation / filtering). */
export const SYNTHETIC_FORM_KEYS = new Set<string>([
  SYNTHETIC_MASTER_PASSWORD_UNLOCKED,
  SYNTHETIC_PREVIEW_BLOCKED,
  SYNTHETIC_CHECKED_URL,
  SYNTHETIC_REPORT_PAGES,
  SYNTHETIC_HAS_POWERBI_CREDENTIAL,
  SYNTHETIC_POWERBI_RESOLVED_LABEL,
  SYNTHETIC_ASSET_DURATION,
  SYNTHETIC_KIOSK_LOCK_REASON,
  SYNTHETIC_POWERBI_PAGES_SELECTED,
  SYNTHETIC_GRAFANA_DASHBOARD_UNLISTED,
]);

/** Prefix used by synthetic keys. Used by `handleSave` to strip them from the payload. */
export const SYNTHETIC_KEY_PREFIX = '_';

/** True if `key` is a synthetic form-only key (named flag OR `_`-prefixed convention). */
export function isReservedFormKey(key: string): boolean {
  return SYNTHETIC_FORM_KEYS.has(key) || key.startsWith(SYNTHETIC_KEY_PREFIX);
}
